import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ESOganisationDropdown from "../components/dropdown/esOrganisationDropdown";
import SectionHeader from "../components/section/SectionHeader";
import {
  ADD_LMR_GROUPS_FORM_FIELDS,
  EDIT_LMR_GROUP_PROFILE_FORM_FIELDS,
  EDIT_LMR_GROUPS_FORM_FIELDS,
  LMR_GROUPS_FILTER_COLUMNS,
  LMR_GROUPS_TABLE_HEADERS,
} from "../constants";
import AuthContext from "../store/authContext";
import {
  errorAlert,
  makeApiRequest,
  transformLMRGroupsRows,
  successAlert,
  transformLMRGroupProfileUpdateBody,
  transformLMRGroupsUpdateBody,
  createSearchConfig,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import OrgContext from "../store/orgContext";
import InfoAlert from "../ui/InfoAlert";
import TableActionButton from "../ui/TableActionButton";
import AdvancedSearch from "../components/advancedSearch/AdvancedSearch";
import {
  getLMRGroupDetailsByRef,
  deleteLMRGroup,
  updateLMRGroupByRef,
  updateLMRGroupProfileByRef,
  getLMRGroupByRef,
  getLMRGroupProfileByRef,
} from "../api";
import {
  INITIAL_LMR_GROUPS_EDIT_STATE,
  INITIAL_LMR_GROUP_PROFILE_EDIT_STATE,
} from "../constants/editState";
import EditGroup from "../components/dataTable/EditGroup";
import useHideSidePanel from "../hooks/useHideSidePanel";
import {
  DELETE_LMR_GROUP_TRANSLATIONS,
  READ_LMR_GROUP_TRANSLATIONS,
} from "../translations/util";
import useAppTitle from "../hooks/useAppTitle";
import { LMRGroupsEdit } from "../api/icCheck";
import SearchContext from "../store/searchContext";
import BulkUpload from "../components/bulkUpload/BulkUpload";

interface ISearchData {
  "TG-ID"?: string | null;
  "LMR-NW-TYPE"?: string | null;
  "MAPPED-MCPTT-GID"?: string | null;
}

const INITIAL_STATE = {
  "TG-ID": "",
  "LMR-NW-TYPE": "",
  "MAPPED-MCPTT-GID": "",
};

const searchFields = ["TG-ID", "LMR-NW-TYPE", "MAPPED-MCPTT-GID"];

const searchConfigNew = createSearchConfig(
  searchFields,
  ADD_LMR_GROUPS_FORM_FIELDS
);

const LMRGroups = () => {
  const {
    token,
    scopes: { lmr_groups: lmrGroups, lmr_group_profile: lmrGroupProfile },
  } = useContext(AuthContext);
  const { orgs: orgList, selectedOrg, updateOrg } = useContext(OrgContext);
  const { selectedColumn, setSelectedColumn, setSearchText, setDisableSearch } =
    useContext(SearchContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  useAppTitle("LMR Groups- IWF Config Admin Tool (ICAT) by AT&T");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [groupsData, setGroupssData] = useState<any[]>([]);

  // State to check if Bulk Upload is Clicked
  const [isBulkUploadEnabled, setIsBulkUploadEnabled] = useState(false);

  const {
    showSidePanel,
    showAdvancedSearch,
    handleToggleAdvanceSearchPanel,
    handleToggleSidePanel,
  } = useHideSidePanel();
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);

  const [searchData, setSearchData] = useState<ISearchData>(INITIAL_STATE);
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    data: ISearchData;
    pageNum: number;
    pageSize: number;
    sortByField?: string;
    sortDirection?: "desc" | "asc";
  }>({
    pageSize: 20,
    pageNum: 1,
    data: {},
    sortByField: undefined,
    sortDirection: undefined,
  });

  const fetchLMRGroups = async () => {
    setLoading(true);
    try {
      if (selectedOrg) {
        const { data, pageNum, pageSize, sortByField, sortDirection } =
          searchQuery;
        const queryParams = new URLSearchParams({
          page: `${pageNum}`,
          pageSize: `${pageSize}`,
          ...(sortByField && { sortByField }),
          ...(sortDirection && { sortDirection }),
          ...(data &&
            data["TG-ID"] && {
              "TG-ID": data["TG-ID"],
            }),
          ...(data &&
            data["LMR-NW-TYPE"] && {
              "LMR-NW-TYPE": data["LMR-NW-TYPE"],
            }),
          ...(data &&
            data["MAPPED-MCPTT-GID"] && {
              "MAPPED-MCPTT-GID": data["MAPPED-MCPTT-GID"],
            }),
        });
        const response = await makeApiRequest<{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
        }>({
          path: `/api/v1/lmr-groups?${queryParams}`,
          token,
          org: selectedOrg.id,
        });
        setNumberOfRows(response.NbrOfRows);
        setNumberOfPages(response.NbrOfPages);
        setGroupssData(response.Rows);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  const sortTable = (col: string, direction: "asc" | "desc") => () => {
    setSearhQuery({
      ...searchQuery,
      sortByField: col,
      sortDirection: direction,
    });
  };

  const handleLMRGroupDelete = async (ref: string) => {
    if (selectedOrg && token) {
      try {
        await deleteLMRGroup({
          ref,
          org: selectedOrg.id,
          token,
        });
        successAlert(DELETE_LMR_GROUP_TRANSLATIONS.successAlertMessage);
        handleToggleSidePanel();
        setSelectedConnectionId(null);
        fetchLMRGroups();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        errorAlert(error);
      }
    }
  };

  useEffect(() => {
    fetchLMRGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, searchQuery]);

  useEffect(() => {
    if (!showAdvancedSearch && Object.keys(searchQuery.data).length === 0) {
      setSearchData(INITIAL_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvancedSearch]);

  useEffect(() => {
    setSelectedColumn(null);
    setSearchText("");
    setDisableSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchData(INITIAL_STATE);
    setSearhQuery({
      pageSize: 20,
      pageNum: 1,
      data: {},
      sortByField: undefined,
      sortDirection: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  return (
    <>
      <SectionHeader
        pageTitle={READ_LMR_GROUP_TRANSLATIONS.header}
        pageDescription={READ_LMR_GROUP_TRANSLATIONS.headerSubtext}
        sideComponent={
          <div className="dropdown-details">
            <ESOganisationDropdown
              org={selectedOrg || undefined}
              orgsList={orgList}
              setOrg={updateOrg}
            />
          </div>
        }
      />

      {selectedOrg ? (
        <>
          {showAdvancedSearch ? (
            <AdvancedSearch
              setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
              advancedSearchState={searchData}
              advancedSearchConfig={searchConfigNew}
              setAdvancedSearchState={setSearchData}
              onSubmit={(data) =>
                setSearhQuery({
                  ...searchQuery,
                  pageNum: 1,
                  pageSize: 20,
                  data,
                })
              }
              onCancel={() => {
                setSearchData(INITIAL_STATE);
                handleToggleAdvanceSearchPanel();
              }}
            />
          ) : null}

          {showSidePanel && isBulkUploadEnabled ? (
            <BulkUpload
              moduleName="LMR Group"
              pathName="/api/v1/lmr-groups"
              csvTemplateFile={["TG-ID,LMR-NW-TYPE,MAPPED-MCPTT-GID"]}
              closeBulkUploadSidePanel={() => {
                handleToggleSidePanel();
                setIsBulkUploadEnabled(false);
                fetchLMRGroups();
              }}
            />
          ) : null}

          {showSidePanel &&
          selectedOrg &&
          selectedConnectionId &&
          !isBulkUploadEnabled ? (
            <EditGroup
              panelName={READ_LMR_GROUP_TRANSLATIONS.editRow.panelName}
              initialGroupState={INITIAL_LMR_GROUPS_EDIT_STATE}
              initialGroupProfileState={INITIAL_LMR_GROUP_PROFILE_EDIT_STATE}
              org={selectedOrg.id}
              esRef={selectedConnectionId}
              setShowEditPanel={handleToggleSidePanel}
              onDelete={() => handleLMRGroupDelete(selectedConnectionId)}
              groupFormFields={EDIT_LMR_GROUPS_FORM_FIELDS}
              groupProfileFormFields={EDIT_LMR_GROUP_PROFILE_FORM_FIELDS}
              transformGroupProfileUpdateBody={
                transformLMRGroupProfileUpdateBody
              }
              transformGroupsUpdateBody={transformLMRGroupsUpdateBody}
              header={READ_LMR_GROUP_TRANSLATIONS.editRow.header}
              IcCheckAPI={LMRGroupsEdit}
              onGroupUpdate={updateLMRGroupByRef}
              onGroupProfileUpdate={updateLMRGroupProfileByRef}
              loadDataByRef={getLMRGroupDetailsByRef}
              fetchGroupData={getLMRGroupByRef}
              fetchGroupProfileData={getLMRGroupProfileByRef}
              hasDeletePermission={lmrGroups && lmrGroups.includes("d")}
              hasGroupEditPermission={lmrGroups && lmrGroups.includes("u")}
              hasGroupProfileEditPermission={
                lmrGroupProfile && lmrGroupProfile.includes("u")
              }
              hasGroupProfileViewPermission={
                lmrGroupProfile && lmrGroupProfile.includes("r")
              }
              refreshTable={fetchLMRGroups}
              deleteModalItemName="TG-ID"
            />
          ) : null}

          <DataTableWithControls
            loading={loading}
            filterColumns={LMR_GROUPS_FILTER_COLUMNS}
            selectedColumn={selectedColumn}
            setSelectedColumn={setSelectedColumn}
            onSearch={(term) =>
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: { [`${selectedColumn?.id}`]: term },
              })
            }
            setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
            showCreateButton={lmrGroups?.includes("c")}
            onCreateButtonClick={() => {
              navigate(`/configurationManagement/lmrgroups/new`);
            }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rows={transformLMRGroupsRows(groupsData).map((row: any) => ({
              ...row,
              action: (
                <TableActionButton
                  onActionButtonClick={() => {
                    setSelectedConnectionId(row.id);
                    handleToggleSidePanel();
                  }}
                />
              ),
            }))}
            numberOfAdvancedSearchFilters={searchConfigNew.reduce(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (acc, el: any) => {
                const { name } = el;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const obj: any = { ...searchData };
                return acc + (obj[name] && obj[name].length > 0 ? 1 : 0);
              },
              0
            )}
            clearAdvancedSearchFilters={() => {
              setSearchData(INITIAL_STATE);
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: {},
              });
            }}
            headers={LMR_GROUPS_TABLE_HEADERS}
            pageNum={searchQuery.pageNum}
            setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
            totalItems={numberOfRows}
            totalPages={numberOfPages}
            pageSize={searchQuery.pageSize}
            setPageSize={(pageSize) =>
              setSearhQuery({ ...searchQuery, pageSize })
            }
            sortByField={searchQuery.sortByField}
            sortDirection={searchQuery.sortDirection}
            handleSort={sortTable}
            showBulkUploadButton={lmrGroups && lmrGroups.includes("c")}
            onBulkUploadButtonClick={() => {
              handleToggleSidePanel();
              setIsBulkUploadEnabled(true);
            }}
          />
        </>
      ) : (
        <InfoAlert
          heading={READ_LMR_GROUP_TRANSLATIONS.infoAlert.heading}
          subText={READ_LMR_GROUP_TRANSLATIONS.infoAlert.subText}
        />
      )}
    </>
  );
};

export default LMRGroups;
